import { useRegisterSW } from "virtual:pwa-register/react";
import { createContext, useContext, useEffect, useState } from "react";
import { replaceUrl } from "~lib/browserNavigation.ts";

import { envFromHostname } from "~lib/envHelpers";
import { useFirstPainted } from "~lib/firstPaint.ts";
import { handleError } from "~lib/handleError.ts";
import { reloadWithFreshShell } from "~lib/reloadWithFreshShell.ts";

const IS_PRODUCTION = envFromHostname() === "production";

const UPDATE_CHECK_INTERVAL = 1000 * 60 * 60 * 12;

interface UpdateProviderProps {
  children: React.ReactNode;
}

const UpdateContext = createContext({
  updateAvailable: false,
  updateNow: () => {},
});

// Precaching a new release competes with the start requests for the same connections, so this only mounts after the first paint
function ServiceWorkerRegistrar({
  onNeedRefresh,
}: {
  onNeedRefresh: (needRefresh: boolean) => void;
}) {
  const {
    needRefresh: [needRefresh],
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => {
          // A failed update check retries in 12h; reporting it is pure noise
          if (navigator.onLine) void registration.update().catch(() => {});
        }, UPDATE_CHECK_INTERVAL);
      }
    },
    onRegisterError: handleError,
  });

  useEffect(() => {
    onNeedRefresh(needRefresh);
  }, [needRefresh, onNeedRefresh]);

  return null;
}

export function UpdateProvider({ children }: UpdateProviderProps) {
  const painted = useFirstPainted();
  const [needRefresh, setNeedRefresh] = useState(false);

  const isSimulation =
    !IS_PRODUCTION &&
    new URLSearchParams(window.location.search).has("simulateUpdate");

  const updateNow = () => {
    if (isSimulation) {
      const url = new URL(window.location.href);
      url.searchParams.delete("simulateUpdate");
      replaceUrl(url.toString());
    } else {
      void reloadWithFreshShell();
    }
  };

  const value = {
    updateAvailable: needRefresh || isSimulation,
    updateNow,
  };

  return (
    <UpdateContext.Provider value={value}>
      {painted && <ServiceWorkerRegistrar onNeedRefresh={setNeedRefresh} />}
      {children}
    </UpdateContext.Provider>
  );
}

export function useUpdate() {
  return useContext(UpdateContext);
}
